import { api } from '@/api/client'
import { resolveCode, type ScanResult } from '@/features/scan/api'

/** Where a scanned code sends the shelf. */
export type ShelfTarget =
  | { kind: 'drawer'; drawerId: string; lotId?: string }
  | { kind: 'home'; q?: string }
  | { kind: 'part'; partId: string; lotId?: string }
  | { kind: 'sheet'; result: ScanResult }
  | { kind: 'unknown' }

/**
 * A scanned code, turned into a place on the shelf (D38). Everything that has a
 * drawer opens that drawer; a part in several places becomes a search that
 * lights all of them; a distributor label for a part not yet in the inventory
 * keeps the desk's import sheet.
 */
export async function resolveForShelf(code: string): Promise<ShelfTarget> {
  const found = await resolveCode(code)

  switch (found.kind) {
    case 'storage_location': {
      if (!found.id) return { kind: 'unknown' }
      const location = await getLocation(found.id)
      // A cabinet's own label opens the maps; anything else is its own "drawer".
      if (location?.grid_rows != null) return { kind: 'home' }
      return { kind: 'drawer', drawerId: found.id }
    }
    case 'part_lot': {
      if (!found.id) return { kind: 'unknown' }
      const lot = await getLot(found.id)
      if (lot?.location_id) return { kind: 'drawer', drawerId: lot.location_id, lotId: found.id }
      return found.part_id
        ? { kind: 'part', partId: found.part_id, lotId: found.id }
        : { kind: 'unknown' }
    }
    case 'part':
      return found.id ? partTarget(found.id) : { kind: 'unknown' }
    case 'vendor_label':
      return found.matched_part_id
        ? partTarget(found.matched_part_id)
        : { kind: 'sheet', result: found }
    default:
      return { kind: 'unknown' }
  }
}

async function partTarget(partId: string): Promise<ShelfTarget> {
  const { data } = await api.GET('/api/parts/{id}', { params: { path: { id: partId } } })
  if (!data) return { kind: 'unknown' }
  const placed = data.lots.filter((lot) => lot.location_id)
  const places = new Set(placed.map((lot) => lot.location_id))
  if (places.size === 1 && placed[0]?.location_id) {
    return {
      kind: 'drawer',
      drawerId: placed[0].location_id,
      lotId: placed.length === 1 ? placed[0].id : undefined,
    }
  }
  if (places.size === 0) return { kind: 'part', partId }
  return { kind: 'home', q: data.name }
}

async function getLocation(id: string) {
  const { data } = await api.GET('/api/locations/{id}', { params: { path: { id } } })
  return data
}

async function getLot(id: string) {
  const { data } = await api.GET('/api/lots/{id}', { params: { path: { id } } })
  return data
}
